import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, TYPE_META, TYPE_ORDER } from "@/lib/api";
import { motion } from "framer-motion";
import { Link as LinkIcon, Image as ImgIcon, Check, Share2, ListOrdered } from "lucide-react";
import { toast } from "sonner";
import ImportedList from "@/components/ImportedList";

const TYPES = TYPE_ORDER;

// Extract first URL anywhere in a string (Android shares often dump text like
// "Look at this https://… cool right?")
function extractUrl(s) {
  if (!s) return "";
  const m = s.match(/https?:\/\/\S+/);
  return m ? m[0].replace(/[)\].,!?]+$/, "") : "";
}

export default function Add() {
  const [params] = useSearchParams();
  // From PWA share_target: ?url=...&text=...&title=...
  const sharedUrl = params.get("url") || "";
  const sharedText = params.get("text") || "";
  const sharedTitle = params.get("title") || "";
  const initialUrl = sharedUrl || extractUrl(sharedText) || extractUrl(sharedTitle) || "";
  const isShare = Boolean(initialUrl && (sharedUrl || sharedText || sharedTitle));

  const [mode, setMode] = useState("link");
  const [url, setUrl] = useState(initialUrl);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [expanded, setExpanded] = useState(null); // { kind, source_url, items: [] }
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const navigate = useNavigate();
  const submittedShare = useRef(false);

  // Auto-submit if shared via Share Target with a URL. This intentionally
  // runs ONCE at mount — depending on `initialUrl` / `handleLinkSubmit` would
  // re-fire the submission after the URL is cleared from history below and
  // resubmit again. The submittedShare ref guarantees single-fire safety.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initialUrl && !submittedShare.current && initialUrl.match(/^https?:\/\//)) {
      submittedShare.current = true;
      // Clean the URL out of the address bar so reload doesn't re-trigger
      window.history.replaceState({}, document.title, "/add");
      handleLinkSubmit(null, initialUrl);
    }
  }, []);

  async function handleLinkSubmit(e, override) {
    if (e) e.preventDefault();
    const u = (override || url).trim();
    if (!u) { toast.error("Paste a link first"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/items/from-link", { url: u });
      if (r.data && r.data.expanded) {
        setExpanded(r.data);
        toast.success(`Filed ${r.data.created} item${r.data.created === 1 ? "" : "s"} from that list`);
      } else {
        setPreview(r.data);
        toast.success("Filed to your library");
      }
    } catch (err) {
      console.error("[add:link] submit failed:", err);
      toast.error("Could not read that link");
    } finally {
      setSubmitting(false);
    }
  }

  function acceptImage(f) {
    if (!f) return;
    if (!f.type || !f.type.startsWith("image/")) {
      toast.error("that's not an image");
      return;
    }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  }

  function onFile(e) {
    acceptImage(e.target.files?.[0]);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    acceptImage(f);
  }

  // Paste from clipboard — only wired while the screenshot mode is open so
  // it doesn't intercept text pasting elsewhere in the app.
  useEffect(() => {
    if (mode !== "screenshot") return;
    function onPaste(e) {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            acceptImage(f);
            return;
          }
        }
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [mode]);

  async function handleScreenshotSubmit(e) {
    e.preventDefault();
    if (!file) { toast.error("Choose a screenshot"); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/items/from-screenshot", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success("Screenshot read & filed");
    } catch (e) {
      console.error("[add:screenshot] submit failed:", e);
      toast.error("Could not read the screenshot");
    } finally {
      setSubmitting(false);
    }
  }

  async function correctType(t) {
    if (!preview) return;
    const r = await api.patch(`/items/${preview.id}`, { type: t });
    setPreview(r.data);
    toast.success("Category updated");
  }

  if (expanded) {
    const kindLabel = expanded.kind === "youtube" ? "YouTube playlist"
                    : expanded.kind === "letterboxd" ? "Letterboxd list"
                    : "list";
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto space-y-8" data-testid="expanded-card">
        <div className="flex items-center gap-3">
          <ListOrdered size={18} strokeWidth={1.4} className="text-[var(--bronze)]"/>
          <span className="stamp" style={{ "--stamp-color": "var(--bronze)" }}>List unrolled</span>
        </div>
        <h1 className="font-serif-lit text-4xl tracking-tight text-[var(--ink)]">
          Filed <span className="italic">{expanded.created}</span> item{expanded.created === 1 ? "" : "s"} from that {kindLabel}.
        </h1>
        <p className="font-garamond italic text-[var(--ink-dusty)]">
          Each one became its own shelf entry. Re-shelve or remove any that look wrong.
        </p>
        <ImportedList items={expanded.items} />
        <div className="flex gap-3 pt-4">
          <button className="lc-btn" data-testid="expanded-done-btn" onClick={() => navigate("/")}>Take me to the library</button>
          <button className="lc-btn-ghost" data-testid="expanded-another-btn" onClick={() => { setExpanded(null); setUrl(""); }}>Add another</button>
        </div>
      </motion.div>
    );
  }

  if (preview) {
    return (
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto" data-testid="confirm-card">
        <span className="stamp">Filed</span>
        <h1 className="mt-4 font-serif-lit text-4xl tracking-tight text-[var(--ink)]">Looks like a <span className="italic">{TYPE_META[preview.type]?.label?.toLowerCase() || preview.type}</span>.</h1>
        <p className="mt-2 text-[var(--ink-dusty)]">Tap to confirm, or pick another shelf.</p>

        <div className="mt-8 placed-card overflow-hidden relative">
          <span className="tape-corner"></span>
          {preview.image_url && (
            <img
              src={preview.image_url}
              alt=""
              className={preview.type === "picture"
                ? "block w-full max-h-[420px] object-contain bg-[var(--paper-aged)]"
                : "block w-full max-h-80 object-cover"}
            />
          )}
          <div className="p-6">
            <h2 className="font-serif-lit text-2xl text-[var(--ink)]">{preview.title || "Untitled"}</h2>
            {preview.creator && <p className="text-[var(--ink-dusty)] italic">{preview.creator}</p>}
            {preview.excerpt && <p className="mt-3 text-sm text-[var(--ink-dusty)] line-clamp-3">{preview.excerpt}</p>}
          </div>
        </div>

        <div className="mt-8">
          <p className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)] mb-3">If we guessed wrong, pick the shelf</p>
          <div className="flex flex-wrap gap-2">
            {TYPES.map(t => (
              <button key={t} data-testid={`correct-${t}`} onClick={() => correctType(t)} className={`chip ${preview.type === t ? "active" : ""}`}>
                {TYPE_META[t]?.label || t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 flex gap-3">
          <button className="lc-btn flex items-center gap-2" data-testid="confirm-keep-btn" onClick={() => navigate(`/item/${preview.id}`)}>
            <Check size={16}/> Keep on the shelf
          </button>
          <button className="lc-btn-ghost" data-testid="add-another-btn" onClick={() => { setPreview(null); setUrl(""); setFile(null); setFilePreview(null); }}>
            Add another
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="add-page">
      {/* "Shared in" banner — when coming via Web Share Target */}
      {isShare && !preview && (
        <div className="placed-card flex items-start gap-3 p-5 mb-8" data-testid="share-banner"
             style={{ borderColor: "var(--bronze)" }}>
          <Share2 size={16} className="mt-1 text-[var(--bronze)]" strokeWidth={1.4}/>
          <div>
            <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--bronze)]">Shared in</p>
            <p className="mt-1 font-garamond italic text-[var(--ink-dusty)]">
              {submitting ? "Reading what you sent…" : "Looking up what you sent…"}
            </p>
          </div>
        </div>
      )}

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <span className="stamp">Add to library</span>
        <h1 className="mt-5 font-serif-lit text-5xl tracking-tight text-[var(--ink)] leading-tight">
          Share a thing <span className="italic">you love</span>.
        </h1>
        <p className="mt-4 text-[var(--ink-dusty)]">Paste a link, or drop in a screenshot. We&apos;ll figure out where it belongs.</p>
      </motion.div>

      <div className="mt-10 flex gap-2">
        <button data-testid="mode-link" onClick={() => setMode("link")} className={`chip ${mode === "link" ? "active" : ""}`}>
          <LinkIcon size={12} strokeWidth={1.6}/> Paste link
        </button>
        <button data-testid="mode-screenshot" onClick={() => setMode("screenshot")} className={`chip ${mode === "screenshot" ? "active" : ""}`}>
          <ImgIcon size={12} strokeWidth={1.6}/> Screenshot
        </button>
      </div>

      {mode === "link" && (
        <form onSubmit={handleLinkSubmit} className="mt-10 space-y-8" data-testid="link-form">
          <div>
            <label className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)]">URL</label>
            <input
              type="url"
              data-testid="link-input"
              className="lc-input mt-2"
              placeholder="https://…"
              value={url}
              onChange={e => setUrl(e.target.value)}
              autoFocus
            />
          </div>
          <button type="submit" disabled={submitting} className="lc-btn" data-testid="link-submit">
            {submitting ? "Reading…" : "File it"}
          </button>
        </form>
      )}

      {mode === "screenshot" && (
        <form onSubmit={handleScreenshotSubmit} className="mt-10 space-y-8" data-testid="screenshot-form">
          <div>
            <span className="font-mono-lc text-[10px] tracking-[0.22em] uppercase text-[var(--ink-dusty)]">Screenshot</span>
            {/* Drop zone — accepts drag/drop, click-to-browse, and paste from
                clipboard (Cmd+V / Ctrl+V while this screen is open). The
                actual <input type="file"> is hidden but the whole card is a
                <label> so it stays keyboard-accessible for screen readers. */}
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              data-testid="screenshot-dropzone"
              className={`mt-3 block cursor-pointer rounded-xl border-2 border-dashed p-8 sm:p-10 text-center transition-colors ${
                dragOver
                  ? "border-[var(--bronze)] bg-[var(--paper-aged)]"
                  : "border-[rgba(179,137,90,0.32)] hover:border-[var(--bronze)] hover:bg-[var(--paper-aged)]/50"
              }`}
            >
              <input
                type="file"
                accept="image/*"
                data-testid="screenshot-input"
                onChange={onFile}
                className="sr-only"
              />
              <ImgIcon size={22} strokeWidth={1.4} className="mx-auto text-[var(--bronze)]" />
              <p className="mt-3 font-serif-lit text-lg text-[var(--ink)]">
                drop a screenshot here
              </p>
              <p className="mt-1 font-garamond italic text-sm text-[var(--ink-dusty)]">
                or click to browse — you can also paste from your clipboard
              </p>
              <p className="mt-3 font-mono-lc text-[9px] tracking-[0.24em] uppercase text-[var(--ink-light)]">
                jpg · png · webp · gif · heic · up to 8 mb
              </p>
            </label>
          </div>
          {filePreview && (
            <div className="placed-card overflow-hidden" data-testid="screenshot-preview">
              <img src={filePreview} alt="" className="w-full max-h-80 object-contain bg-[var(--paper-aged)]"/>
            </div>
          )}
          <button type="submit" disabled={submitting || !file} className="lc-btn" data-testid="screenshot-submit">
            {submitting ? "Reading the picture…" : "Read & file"}
          </button>
          <p className="font-mono-lc text-[10px] tracking-[0.18em] uppercase text-[var(--ink-light)]">
            Powered quietly by Claude vision · classification is a guess you can correct
          </p>
        </form>
      )}
    </div>
  );
}
